import type { EloRating, Question } from "../types.js";
import { buildCurriculumGraph } from "../graph/curriculum.js";
import { DEFAULT_LOCALE, getCopy, type Locale, type LocaleStrings } from "./conceptCopy.js";
import {
  distractorModeForRating,
  pickDistractorsByMode,
  type DistractorMode,
} from "./pickDistractors.js";

const DEFAULT_RNG: () => number = () => Math.random();

let idCounter = 0;

export type GenerateMcqOptions = {
  rating: EloRating;
  rng?: () => number;
  id?: string;
  graph?: import("../graph/digraph.js").DiGraph;
  locale?: Locale;
  /** Override the auto-derived distractor mode (`distractorModeForRating`). */
  distractorMode?: DistractorMode;
  /** Override the stem template index. Default: deterministic from `rng`. */
  stemIndex?: number;
  /**
   * Resolved copy (titles/blurbs/stems) for the active subject. Defaults to the
   * AI-literacy `getCopy(locale)`. Pass a `SubjectPack`'s copy so generation works
   * for non-AI-literacy concepts (first aid, notany, custom packs).
   */
  copy?: LocaleStrings;
};

/**
 * Produces a 4-way MCQ matching a `blurb` to its concept title.
 *
 * The stem rotates across `getCopy(locale).stems`, and distractors are picked with a
 * mode derived from `rating` (`< 1200` → far, `> 1500` → near, else mixed) so a
 * generated question’s difficulty matches its Elo rating instead of always being
 * “nearest neighbour”.
 */
export function generateMcqFromConcept(
  correctConceptId: string,
  options: GenerateMcqOptions = { rating: 1200 }
): Question {
  const rng = options.rng ?? DEFAULT_RNG;
  const g = options.graph ?? buildCurriculumGraph();
  const copy: LocaleStrings = options.copy ?? getCopy(options.locale ?? DEFAULT_LOCALE);

  const correctTitle = copy.titles[correctConceptId];
  const blurb = copy.blurbs[correctConceptId];
  if (!correctTitle || !blurb) {
    throw new Error(
      `unknown concept for locale ${options.locale ?? DEFAULT_LOCALE}: ${correctConceptId}`
    );
  }

  const mode = options.distractorMode ?? distractorModeForRating(options.rating);
  const distractorIds = pickDistractorsByMode(g, correctConceptId, 3, mode, rng);
  const distractorLabels = distractorIds.map((id) => copy.titles[id] ?? id);

  const allLabels = [correctTitle, ...distractorLabels] as [string, string, string, string];
  const perm = shuffledIndexOrder(4, rng);
  const optionsPermuted = perm.map((i) => allLabels[i]!) as [string, string, string, string];
  const correctIndex = perm.indexOf(0);
  if (correctIndex < 0) throw new Error("permutation error");

  const stemIdx =
    options.stemIndex !== undefined
      ? Math.abs(options.stemIndex) % copy.stems.length
      : Math.floor(rng() * copy.stems.length);
  const text = copy.stems[stemIdx]!(blurb);

  const id =
    options.id ??
    `g-${correctConceptId}-${(idCounter = (idCounter + 1) % 1_000_000_000)}-${Math.floor(
      rng() * 1e6
    )}`;

  return {
    id,
    rating: options.rating,
    conceptId: correctConceptId,
    text,
    correctIndex: correctIndex as 0 | 1 | 2 | 3,
    options: optionsPermuted,
    tag: "generated",
    source: "generated",
  };
}

function shuffledIndexOrder(n: number, rng: () => number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}
